/**
 * CR456: Image Capture Studio — /admin/image-capture
 * Tabs: Adapters | Source Mapping | Validation
 * CR458: Phase 2 — deep-link support via ?sourceId=<id>&tab=<adapters|mapping|validation>
 * CR459: Merchant-first IA — Image Capture lives under Merchants.
 *        Use useSearchParams (not window.location). Add FLYER source selector.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, Layers, ShieldCheck, ScanLine, AlertTriangle } from "lucide-react";
import { AdaptersTab } from "@/components/admin/image-capture-studio/AdaptersTab";
import { SourceMappingTab } from "@/components/admin/image-capture-studio/SourceMappingTab";
import { ValidationTab } from "@/components/admin/image-capture-studio/ValidationTab";
import { CaptureRunTriagePanel } from "@/components/admin/batch-runs/CaptureRunTriagePanel";
import { useFlyerSources } from "@/components/admin/image-capture-studio/useFlyerSources";

const NONE_SOURCE = "__none__";

export default function ImageCaptureStudio() {
  const { isAdmin } = useAuth();

  // CR459: Use router-aware search params instead of window.location
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab =
    tabParam === "mapping" ? "mapping"
    : tabParam === "validation" ? "validation"
    : tabParam === "triage" ? "triage"
    : "adapters";
  const deepLinkSourceId = searchParams.get("sourceId") ?? undefined;

  const [activeTab, setActiveTab] = useState(initialTab);

  // CR459: FLYER source selector — selecting a source jumps straight to its mapping
  const { sources } = useFlyerSources();
  const [selectorValue, setSelectorValue] = useState(deepLinkSourceId ?? NONE_SOURCE);

  // Sync selector when deep-link changes (e.g. back/forward nav)
  useEffect(() => {
    setSelectorValue(deepLinkSourceId ?? NONE_SOURCE);
  }, [deepLinkSourceId]);

  const handleSourceSelect = (sourceId: string) => {
    setSelectorValue(sourceId);
    if (sourceId && sourceId !== NONE_SOURCE) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "mapping");
        next.set("sourceId", sourceId);
        return next;
      });
      setActiveTab("mapping");
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("sourceId");
        return next;
      });
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">Admin access required</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header — CR459: Merchant-first copy */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ScanLine className="h-6 w-6" />
              Image Capture
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure per-source image capture adapters for FLYER merchant sources.
              Use the <strong>Adapter Registry</strong> to manage adapters, and{" "}
              <strong>Source Mapping</strong> to assign them to individual sources.
            </p>
          </div>

          {/* CR459: Quick FLYER source jump */}
          <div className="shrink-0 w-64">
            <Select value={selectorValue} onValueChange={handleSourceSelect}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Jump to a FLYER source…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_SOURCE} className="text-muted-foreground">
                  — Select a source —
                </SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s.merchant_source_id} value={s.merchant_source_id}>
                    {s.merchant_name || s.merchant_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Jump directly to a source's mapping
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="adapters" className="gap-1.5">
              <Layers className="h-4 w-4" />
              Adapter Registry
            </TabsTrigger>
            <TabsTrigger value="mapping" className="gap-1.5">
              <Camera className="h-4 w-4" />
              Source Mapping
            </TabsTrigger>
            <TabsTrigger value="validation" className="gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              Validation
            </TabsTrigger>
            <TabsTrigger value="triage" className="gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Run Triage
            </TabsTrigger>
          </TabsList>

          <TabsContent value="adapters" className="mt-6">
            <AdaptersTab />
          </TabsContent>

          <TabsContent value="mapping" className="mt-6">
            {/* CR458: pass deep-link sourceId so SourceMappingTab can auto-open it */}
            <SourceMappingTab preselectedSourceId={deepLinkSourceId} />
          </TabsContent>

          <TabsContent value="validation" className="mt-6">
            <ValidationTab />
          </TabsContent>

          <TabsContent value="triage" className="mt-6">
            <CaptureRunTriagePanel />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
